import { useRef, useEffect } from 'react';
import type { ReplayData } from './ReplayEngine';
import { CANVAS_CONFIG } from '@beat-games/game-config';

interface ReplayPreviewProps {
  replay: ReplayData;
  phaseColor: string;
}

export default function ReplayPreview({ replay, phaseColor }: ReplayPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get the last circle state and cursor position
    const lastCircle = replay.circleStates[replay.circleStates.length - 1];
    const lastPosition = replay.positions[replay.positions.length - 1];

    if (!lastCircle || !lastPosition) return;

    // Clear canvas
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, CANVAS_CONFIG.width, CANVAS_CONFIG.height);

    // Draw circle
    ctx.strokeStyle = phaseColor + '60';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(lastCircle.x, lastCircle.y, lastCircle.radius, 0, Math.PI * 2);
    ctx.stroke();

    // Inner fill
    const fillGradient = ctx.createRadialGradient(
      lastCircle.x, lastCircle.y, 0,
      lastCircle.x, lastCircle.y, lastCircle.radius
    );
    fillGradient.addColorStop(0, phaseColor + '15');
    fillGradient.addColorStop(1, phaseColor + '05');
    ctx.fillStyle = fillGradient;
    ctx.beginPath();
    ctx.arc(lastCircle.x, lastCircle.y, lastCircle.radius, 0, Math.PI * 2);
    ctx.fill();

    // Draw cursor glow
    const cursorGlow = ctx.createRadialGradient(
      lastPosition.x, lastPosition.y, 0,
      lastPosition.x, lastPosition.y, 8
    );
    cursorGlow.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
    cursorGlow.addColorStop(1, 'transparent');
    ctx.fillStyle = cursorGlow;
    ctx.beginPath();
    ctx.arc(lastPosition.x, lastPosition.y, 8, 0, Math.PI * 2);
    ctx.fill();

    // Draw cursor dot
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.beginPath();
    ctx.arc(lastPosition.x, lastPosition.y, 3, 0, Math.PI * 2);
    ctx.fill();

  }, [replay, phaseColor]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_CONFIG.width}
      height={CANVAS_CONFIG.height}
      className="absolute inset-0 w-full h-full"
      style={{ aspectRatio: `${CANVAS_CONFIG.width} / ${CANVAS_CONFIG.height}` }}
    />
  );
}
